'use strict';

const _ = require('lodash');

const io = require('socket.io-client');
const ss = require('socket.io-stream');

const defaultOptions = {
    // whether to reconnect automatically
    reconnection: true,
    // how long to wait before attempting a new reconnection
    reconnectionDelay: 1000,
    // maximum amount of time to wait between reconnections.
    // each attempt increases the reconnection by the amount specified by reconnectionDelay
    reconnectionDelayMax: 5000,
    // connection timeout before a connect_error and connect_timeout events are emitted
    timeout: 20000,
    // when opening a connection, always init a new socket manager instead of using the cached values
    forceNew: true,
};


/**
 * Establishes and represents a WebSockets connection, which sends and receives events from the server.
 */
class Socket {
    /**
     * Creates a new Socket instance, establishing a WebSockets connection to the server
     * @param  {String} serverUrl URL to server
     * @param  {String} appId     App ID
     * @param  {String} token     User token
     * @param  {Object} options   Custom options for the WebSockets connection
     * @return {Socket}           A new Socket instance
     */
    constructor (serverUrl, appId, token, options = { invitations: true }) {
        // if invitations reception config is set, get the value and unset it from the options object
        // (it's not supported for the socket.io-client library)
        this.setOptions(options);
        delete this.options.invitations;

        if (this.useNoCookieServer()) {
            serverUrl = this.getNoCookieServer(serverUrl);
        }

        // detect if the browser has websockets support, copied from Modernizr
        var supports = false;
        try {
            supports = 'WebSocket' in window && window.WebSocket.CLOSING === 2;
        } catch (e) {}

        // if the browser supports WebSockets, only use this transport
        // if not, start with polling
        let transports = ['websocket'];
        if (!supports) {
            transports.unshift('polling');
        }

        // start connection
        this._socket = io.connect(
            serverUrl,
            {
                query: 'appId='+appId+'&token='+token+'&invitations='+options.invitations,
                transports: transports,
            }
        );

        // for keeping socket callback listeners
        this.listenerCallbacks = {};

        this.connected = this._socket.connected;

        // custom ping-pong. Server sends custom-ping, client sends custom-pong
        this.on('custom-ping', () => {
            setTimeout(() => this.send('custom-pong'), 20*1000);
        });

        // Server sends initial-pong, client sends initial-ping
        this.once('initial-pong', function () {
            ICF.Connection.ping = Date.now() - ICF.Connection.initialPingTime;
        });

        // Server sends pong, client sends ping
        this.on('pong', function (ms) {
            ICF.Connection.ping = ms;
        });
    }

    /**
     * Disconnects from server, closing the WebSockets connection
     */
    disconnect () {
        this._socket.disconnect();
    }

    /**
     * Sends an event to the server
     * @param  {String} event     Event name
     * @param  {Object} data      Data to send
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    send (event, data) {
        if (!this._socket) {
            return Promise.reject(new Error('No socket connection active'));
        }

        return this._emitEvent(event, data);
    }

    /**
     * Sends a media resource to the server
     * @param  {Object} data        Data to send
     * @param  {Object} onProgress  callback that runs when a new chunk has been uploaded, @optional
     * @return {Promise<Object>}    A Promise fulfilled with the server response
     */
    upload (data, onProgress) {
        return new Promise((resolve, reject) => {
            const stream = ss.createStream();
            const info = data.info;
            const socketStream = ss(this._socket);

            // begin uploading a media element to the server
            socketStream.emit('media:upload', stream, info);

            const blobStream = ss.createBlobReadStream(data.media);
            let size = 0;

            blobStream.pipe(stream);

            // track file upload progress
            blobStream.on('data', (chunk) => {
                size += chunk.length;
                onProgress && onProgress(Math.floor(size / info.size * 100));
            });

            // when file is fully uploaded in the server, send the actual media creation event
            socketStream.on('media:uploaded', (info) => {
                socketStream.removeAllListeners('media:uploaded');
                resolve(this._emitEvent('media:new', { media: info }));
            });

            socketStream.on('media:uploaded:error', (err) => {
                socketStream.removeAllListeners('media:uploaded');
                reject(err);
            });
        });
    }

    /**
     * Fires the specified callback when the event happens
     * @param  {String}   event     Event name
     * @param  {Function} callback Callback to fire
     */
    on (event, callback) {
        if (!this._socket) {
            throw new Error('No socket connection active');
        }

        // return this._socket.on(event, (data) => {
        //     this._sendACKifNecessary(event, data);
        //     callback(data);
        // });
        return this._socket.on(event, callback);
    }

    /**
    * Fires the specified callback when the event happens once
    * @param  {String}   event     Event name
    * @param  {Function} callback Callback to fire
    */
    once (event, callback) {
        if (!this._socket) {
            throw new Error('No socket connection active');
        }

        // return this._socket.on(event, (data) => {
        //     this._sendACKifNecessary(event, data);
        //     callback(data);
        // });
        return this._socket.once(event, callback);
    }

    /**
     * Removes the listener of the specified event
     * @param  {String}   event     Event name
     * @param  {Function} listener  Listener attached to the event,
     *                              necessary for not removing all listeners with the same name
     */
    removeListener (event, listener) {
        return this._socket.removeListener(event, listener);
    }

    /**
     * Emits an event to the server and waits for the server to respond back
     * with a success or an error message
     * @param  {String} event Event name
     * @param  {Object} data  Data to be attached to the Event
     * @return {Promise<Object|Object>}      A Promise fullfilled with the server response,
     * resolving on success and rejecting on failure
     */
    _emitEvent (event, data) {
        const successKey = event + ':success';
        const errorKey = event + ':error';
        const successPromise = new Promise((resolve) => {
            const successListener = (res) => {
                if (data.eventId === res.eventId) {
                    this.removeListener(event, successListener);
                    return resolve(res);
                }
            };
            this._socket.on(successKey, successListener);
        });
        const errorPromise = new Promise((resolve, reject) => {
            const errorListener = (res) => {
                if (data.eventId === res.eventId) {
                    this.removeListener(event, errorListener);
                    return reject(res);
                }
            };
            this._socket.on(errorKey, errorListener);
        });

        // Emit the event to the server, which will return either
        // success (resolving the Promise) or error (rejecting it)
        this._socket.emit(event, data);
        return Promise.race([successPromise, errorPromise]);
    }

    /**
     * Sends an ACK for confirming the reception of some events
     * @param  {String}   event     Event name
     * @param  {Object}   data      Data to be sent with the ACK
     */
    _sendACKifNecessary (event, data) {
        const ackEvents = [
            'user:join',
            'user:leave',
            'user:invite',
            'message:new',
            'message:read',
            'chat:closed',
            'chat:remove',
            'chat:intervened',
            'invitation:expired',
            'invitations:reject',
            'forever:alone',
            'system:info',
        ];
        // check if the received event has to send an ACK
        if (ackEvents.indexOf(event) !== -1) {
            let chatId = null;
            if (data && !_.isEmpty(data)) {
                chatId = data.chatId || (data.chat && data.chat.id) || (data.message && data.message.chat);
            }
            if (chatId) {
                // Send event ACK to the server for confirming the reception
                data.event = event;
                this.send('events:ack', {
                    chatId: chatId,
                    data: data,
                });
            }
        }
    }

    /**
     * Get the WebSockets connection object
     * @return {Socket}
     */
    getSocket () {
        return this._socket || null;
    }


    /**
     * Get the no cookie server given the "standard" server
     * @param {String} The configured server including the port
     */
    getNoCookieServer (serverUrl) {
        const split = serverUrl.split(':');

        const host = split[1];
        const currentEnv = host.substring(host.lastIndexOf('-') + 1, host.lastIndexOf('.inbenta'));

        const port = split[2] || '';
        return `https://hyperchat-${currentEnv}-nc.inbenta.chat:${port}`;
    }

    /**
     * Sets options, replacing the default ones or adding more if required
     */
    setOptions (options = {}) {
        this.options = Object.assign({}, defaultOptions, options);
    }

    /**
     * Returns whether the no-cookie server should be used
     * Safari and WebView block third party cookies by default and this provokes that the AWSALB cookie is not sent
     * during the handshake, resulting in "Session ID unknown".
     * By forcing the websocket protocol, the first OPTIONS request is not done and handshake is established
     */
    useNoCookieServer () {
        // detect if user is using iOS device
        const isiOS = this.isiOS();
        // detect if user is using Safari browser
        const isSafari = this.isSafariBrowser() || this.isSafariiOS();
        // detect if user is using WebView
        const isWebView = this.isWebViewAndroid() || this.isWebViewiOS();
        // detect if the user is using Windows 8 & Internet Explorer 10
        const isWindows8IE10 = this.isWindows8() && this.isIE10();
        // detect if the user is using Windows 8.1 & Internet Explorer 11
        const isWindows8Dot1IE11 = this.isWindows8Dot1() && this.isIE11();
        // detect if the user is using Windows 7 & Internet Explorer 11
        const isWindows7IE11 = this.isWindows7() && this.isIE11();

        return (isiOS || isSafari || isWebView || isWindows8IE10 || isWindows8Dot1IE11 || isWindows7IE11);
    }

    /**
     * Check if the user browser is Safari
     * @return {Boolean}
     */
    isSafariBrowser () {
        const w = global || window;
        // Safari 3.0+ "[object HTMLElementConstructor]"
        return /constructor/i.test(w.HTMLElement) || (function (p) { return p.toString() === '[object SafariRemoteNotification]'; })(!w['safari'] || safari.pushNotification);
    }

    /**
     * Check if the user browser is Safari and is using iOS
     * @return {Boolean}
     */
    isSafariiOS () {
        const w = global || window;
        return (w.navigator) ? /iP(ad|hone|od).+Version\/[\d\.]+.*Safari/i.test(navigator.userAgent) : false;
    }

    /**
     * Check if the user is using an android WebView
     * @return {Boolean}
     */
    isWebViewAndroid () {
        const w = global || window;
        return (w.navigator) ? /android.+version\/[\d\.]/i.test(navigator.userAgent.toLowerCase()) : false;
    }

    /**
     * Check if the user is using Internet Explorer 10
     * @return {Boolean}
     */
    isIE10 () {
        const w = global || window;
        return (w.navigator) ? (navigator.appVersion && navigator.appVersion.indexOf('MSIE 10') !== -1) : false;
    }

    /**
     * Check if the user is using Internet Explorer 10
     * @return {Boolean}
     */
    isIE11 () {
        const w = global || window;
        return (w.navigator) ? (navigator.appVersion && navigator.appVersion.indexOf('rv:11') !== -1) : false;
    }

    /**
     * Check if the user is using Windows 7
     * @return {Boolean}
     */
    isWindows7 () {
        const w = global || window;
        return (w.navigator) ? (navigator.appVersion && navigator.appVersion.indexOf('NT 6.1') !== -1) : false;
    }

    /**
     * Check if the user is using Windows 8
     * @return {Boolean}
     */
    isWindows8 () {
        const w = global || window;
        return (w.navigator) ? (navigator.appVersion && navigator.appVersion.indexOf('NT 6.2') !== -1) : false;
    }

    /**
     * Check if the user is using Windows 8
     * @return {Boolean}
     */
    isWindows8Dot1 () {
        const w = global || window;
        return (w.navigator) ? (navigator.appVersion && navigator.appVersion.indexOf('NT 6.3') !== -1) : false;
    }

    /**
     * Check if the user is using iOS (iPad/iPhone)
     * @return {Boolean}
     */
    isiOS () {
        const w = global || window;
        return (w.navigator) ? (!!navigator.platform && /iPad|iPhone/.test(navigator.platform)) : false;
    }

    /**
     * Check if the user is using a UIWebView
     * @return {Boolean}
     */
    isWebViewiOS () {
        const w = global || window;
        if (!w.navigator) {
            return false;
        }
        const isStandalone = w.navigator.standalone;
        // is UIWebView
        return this.isiOS() && !this.isSafariBrowser() && !isStandalone;
    }
}

module.exports = Socket;



// WEBPACK FOOTER //
// ./src/Connection/Socket.js